// The pre-badges-3 tools: a CSV of definitions and one SVG per badge in GCS.
// Kept for emergencies, folded away at the bottom of the page.
import React, { useState } from "react";
import { toast } from "react-toastify";
import { apiClient } from "../../../api/client";
import { useAppDispatch } from "../../../store/hooks";
import { baseApi } from "../../../services/rtkApi/baseApi";
import { adminBadgePaths } from "./adminBadgesApi";
import type { AdminBadge } from "./adminTypes";

const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } };

const LegacyTools: React.FC<{ badges: readonly AdminBadge[] }> = ({ badges }) => {
  const dispatch = useAppDispatch();
  const [csv, setCsv] = useState<File | null>(null);
  const [svg, setSvg] = useState<File | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState<"csv" | "svg" | null>(null);
  const codes = [...badges].map((b) => b.code).sort((a, b) => a.localeCompare(b));

  const uploadCsv = async () => {
    if (!csv) return;
    const form = new FormData();
    form.append("file", csv);
    setBusy("csv");
    try {
      await apiClient.post(adminBadgePaths.uploadCsv, form, MULTIPART);
      toast.success("CSV processed.");
      dispatch(baseApi.util.invalidateTags(["Badges"]));
    } catch (error) {
      console.error("Badge CSV upload failed:", error);
      toast.error("The CSV upload failed. See the console.");
    } finally {
      setBusy(null);
    }
  };

  const uploadSvg = async () => {
    if (!svg || !code) return;
    const form = new FormData();
    form.append("image", svg);
    form.append("code", code);
    setBusy("svg");
    try {
      await apiClient.post(adminBadgePaths.uploadImage, form, MULTIPART);
      toast.success(`SVG uploaded for ${code}.`);
    } catch (error) {
      console.error("Badge SVG upload failed:", error);
      toast.error("The SVG upload failed. See the console.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <details className="badm-legacy" aria-label="Legacy tools">
      <summary className="badm-legacy__summary">Legacy tools</summary>
      <div className="badm-legacy__body">
        <p className="badm-note">
          The catalogue now comes from the seed: <code>pnpm badges:seed</code> in n450s_backend upserts every
          definition from <code>modules/badges/catalog.json</code>. The art is composed in the browser from the parts
          library, so nothing here is needed for a new badge.
        </p>

        <div className="badm-legacy__tool">
          <h3 className="badm-legacy__title">Upload definitions (CSV)</h3>
          <p className="badm-muted">
            Upserts by code: name, description, category, tier, points, criteria and unique. It does not know about
            family, threshold, repeatable or active, and the next seed overwrites what it changed.
          </p>
          <div className="badm-legacy__row">
            <input
              type="file"
              accept=".csv,text/csv"
              aria-label="CSV file"
              className="badm-file"
              onChange={(e) => setCsv(e.target.files?.[0] ?? null)}
            />
            <button type="button" className="badm-btn" onClick={uploadCsv} disabled={!csv || busy !== null}>
              {busy === "csv" ? "Uploading" : "Upload CSV"}
            </button>
          </div>
        </div>

        <div className="badm-legacy__tool">
          <h3 className="badm-legacy__title">Upload an SVG to GCS</h3>
          <p className="badm-muted">
            Stores the file as the badge's old image in the badge bucket. Only older app versions and badges the parts
            library cannot draw read it.
          </p>
          <div className="badm-legacy__row">
            <select className="badm-input" aria-label="Badge" value={code} onChange={(e) => setCode(e.target.value)}>
              <option value="">Pick a badge</option>
              {codes.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <input
              type="file"
              accept=".svg,image/svg+xml"
              aria-label="SVG file"
              className="badm-file"
              onChange={(e) => setSvg(e.target.files?.[0] ?? null)}
            />
            <button type="button" className="badm-btn" onClick={uploadSvg} disabled={!svg || !code || busy !== null}>
              {busy === "svg" ? "Uploading" : "Upload SVG"}
            </button>
          </div>
        </div>
      </div>
    </details>
  );
};

export default LegacyTools;
